import React, { useState, useEffect } from 'react';
import { useRef } from 'react';

const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const SearchIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);




const API_URL = '/api';

const ProfileFormModal = ({ open, onClose, onSubmit, initialData, screens }) => {
  const [profile, setProfile] = useState({ 
    ProfileCode: '', 
    ProfileName: '', 
    Permissions: [] 
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenSearchTerm, setScreenSearchTerm] = useState('');
  const [showScreenDropdown, setShowScreenDropdown] = useState(false);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPermissions, setFetchingPermissions] = useState(false);
  
  const screenDropdownRef = useRef(null);

  // Function to fetch profile permissions
  const fetchProfilePermissions = async (profileID) => {
    try {
      setFetchingPermissions(true);
      const response = await fetch(`${API_URL}/profile-permissions/${profileID}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      
      if (!response.ok) {
        throw new Error('Failed to fetch profile permissions');
      }
      
      const permissions = await response.json();
      return permissions;
    } catch (error) {
      console.error('Error fetching profile permissions:', error);
      return [];
    } finally {
      setFetchingPermissions(false);
    }
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      
      const initializeProfile = async () => {
        if (initialData) {
          console.log('Initial data received:', initialData);
          
          // Check if permissions exist in initialData
          if (initialData.Permissions && Array.isArray(initialData.Permissions) && initialData.Permissions.length > 0) {
            console.log('Permissions found in initialData:', initialData.Permissions);
            setProfile({
              ProfileCode: initialData.ProfileCode || '',
              ProfileName: initialData.ProfileName || '',
              Permissions: initialData.Permissions
            });
            
            // Set selected screens from permissions
            const screenIDs = initialData.Permissions.map(p => p.ScreenID);
            setSelectedScreens(screenIDs);
            console.log('Selected screens set to:', screenIDs);
          } else {
            console.log('No permissions found in initialData, fetching permissions...');
            
            // If we have a ProfileID, fetch the permissions
            if (initialData.ProfileID) {
              const permissions = await fetchProfilePermissions(initialData.ProfileID);
              console.log('Fetched permissions:', permissions);
              
              setProfile({
                ProfileCode: initialData.ProfileCode || '',
                ProfileName: initialData.ProfileName || '',
                Permissions: permissions
              });
              
              // Set selected screens from permissions
              const screenIDs = permissions.map(p => p.ScreenID);
              setSelectedScreens(screenIDs);
              console.log('Selected screens set to:', screenIDs);
            } else {
              console.log('No ProfileID in initialData, using empty permissions');
              setProfile({
                ProfileCode: initialData.ProfileCode || '',
                ProfileName: initialData.ProfileName || '',
                Permissions: []
              });
              setSelectedScreens([]);
            }
          }
        } else {
          console.log('No initial data, using empty profile');
          setProfile({
            ProfileCode: '',
            ProfileName: '',
            Permissions: []
          });
          setSelectedScreens([]);
        }
        
        setErrors({});
        setLoading(false);
      };
      
      initializeProfile();
    }
  }, [open, initialData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (screenDropdownRef.current && !screenDropdownRef.current.contains(event.target)) {
        setShowScreenDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleScreenSelect = (screen) => {
    if (!selectedScreens.includes(screen.ScreenID)) {
      setSelectedScreens(prev => [...prev, screen.ScreenID]);
      
      // Add screen to permissions with default values
      setProfile(prev => ({
        ...prev,
        Permissions: [...prev.Permissions, {
          ScreenID: screen.ScreenID,
          ScreenName: screen.ScreenName,
          CanView: false,
          CanCreate: false,
          CanEdit: false,
          CanDelete: false
        }]
      }));
    }
    setShowScreenDropdown(false);
    setScreenSearchTerm('');
  };

  const handleRemoveScreen = (screenID) => {
    setSelectedScreens(prev => prev.filter(id => id !== screenID));
    setProfile(prev => ({
      ...prev,
      Permissions: prev.Permissions.filter(p => p.ScreenID !== screenID)
    }));
  };

  const handlePermissionChange = (screenID, permissionType, value) => {
    setProfile(prev => ({
      ...prev,
      Permissions: prev.Permissions.map(perm => 
        perm.ScreenID === screenID ? { ...perm, [permissionType]: value } : perm
      )
    }));
  };

  const validate = () => {
    const errs = {};
    if (!profile.ProfileCode) errs.ProfileCode = 'Required';
    if (!profile.ProfileName) errs.ProfileName = 'Required';
    
    // Check if at least one screen is selected
    if (selectedScreens.length === 0) {
      errs.screens = 'At least one screen must be selected';
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
        // Format the data for the backend
        const formattedData = {
            ProfileCode: profile.ProfileCode,
            ProfileName: profile.ProfileName,
            Permissions: profile.Permissions.map(perm => ({
                ScreenID: perm.ScreenID,
                CanView: perm.CanView,
                CanCreate: perm.CanCreate,
                CanEdit: perm.CanEdit,
                CanDelete: perm.CanDelete
            }))
        };
        
        console.log('Submitting data:', formattedData); // Debug log
        
        // Call the onSubmit function passed from parent
        await onSubmit(formattedData);
    } catch (error) {
        console.error('Error submitting profile:', error);
        alert(`Error saving profile: ${error.message}`);
    } finally {
        setIsSubmitting(false);
    }
};

  // Filter screens based on search term and exclude already selected screens
  const filteredScreens = (screens || []).filter(screen => 
    screen && 
    (screen.ScreenName.toLowerCase().includes(screenSearchTerm.toLowerCase()) ||
     screen.ScreenCode.toLowerCase().includes(screenSearchTerm.toLowerCase())) &&
    !selectedScreens.includes(screen.ScreenID)
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-[#E2E8F0]">
          <h2 className="text-2xl font-bold text-[#384959]">
            {initialData ? 'Edit Profile' : 'Create New Profile'}
          </h2>
          <button
            className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
            onClick={onClose}
          >
            <CloseIcon size={24} />
          </button>
        </div>
        
        <div className="flex-grow overflow-y-auto p-6">
          {loading || fetchingPermissions ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A89A7] mx-auto"></div>
                <p className="mt-4 text-gray-600">
                  {fetchingPermissions ? 'Loading permissions...' : 'Loading profile data...'}
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">
                    Profile Code *
                  </label>
                  <input
                    name="ProfileCode"
                    value={profile.ProfileCode}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.ProfileCode ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.ProfileCode && <span className="text-xs text-red-500">{errors.ProfileCode}</span>}
                </div>
                
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">
                    Profile Name *
                  </label>
                  <input
                    name="ProfileName"
                    value={profile.ProfileName}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.ProfileName ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                  />
                  {errors.ProfileName && <span className="text-xs text-red-500">{errors.ProfileName}</span>}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Screen Permissions</h3>
                {errors.screens && <span className="text-xs text-red-500 block mb-2">{errors.screens}</span>}
                
                {/* Screen Selection Dropdown */}
                <div className="mb-4" ref={screenDropdownRef}>
                  <label className="block text-sm font-medium text-[#64748b] mb-2">
                    Add Screens *
                  </label>
                  <div className="relative">
                    <div
                      className="w-full border rounded-lg px-3 py-2 flex justify-between items-center cursor-pointer border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      onClick={() => setShowScreenDropdown(!showScreenDropdown)}
                    >
                      <span className="text-gray-500">Select screens to add...</span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                    
                    {showScreenDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg">
                        <div className="p-2 border-b border-[#E2E8F0]">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search screens..."
                              className="w-full pl-8 pr-4 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                              value={screenSearchTerm}
                              onChange={(e) => setScreenSearchTerm(e.target.value)}
                            />
                            <SearchIcon className="absolute left-2.5 top-2.5 text-gray-400" />
                          </div>
                        </div>
                        
                        <div className="max-h-60 overflow-y-auto">
                          {filteredScreens.length > 0 ? (
                            filteredScreens.map(screen => (
                              <div
                                key={screen.ScreenID}
                                className="px-4 py-2 hover:bg-[#F9FAFB] cursor-pointer flex items-center"
                                onClick={() => handleScreenSelect(screen)}
                              >
                                <PlusIcon className="mr-2 text-[#6A89A7]" />
                                {screen.ScreenCode} - {screen.ScreenName}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500 text-center">
                              {screens.length === 0 ? 'No screens available' : 'No screens found'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Selected Screens with Permissions */}
                {selectedScreens.length > 0 && (
                  <div className="bg-[#F9FAFB] rounded-xl border border-[#E2E8F0] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-[#E2E8F0]">
                        <thead className="bg-[#F1F5F9]">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[#64748b] uppercase">Screen</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-[#64748b] uppercase">View</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-[#64748b] uppercase">Create</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-[#64748b] uppercase">Edit</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-[#64748b] uppercase">Delete</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-[#64748b] uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2E8F0]">
                          {profile.Permissions.map((permission) => (
                            <tr key={permission.ScreenID} className="hover:bg-[#F9FAFB]">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm font-medium text-[#384959]">
                                  {permission.ScreenName}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={permission.CanView || false}
                                  onChange={(e) => handlePermissionChange(permission.ScreenID, 'CanView', e.target.checked)}
                                  className="h-4 w-4 text-[#6A89A7] rounded focus:ring-[#BDDDFC]"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={permission.CanCreate || false}
                                  onChange={(e) => handlePermissionChange(permission.ScreenID, 'CanCreate', e.target.checked)}
                                  className="h-4 w-4 text-[#6A89A7] rounded focus:ring-[#BDDDFC]"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={permission.CanEdit || false}
                                  onChange={(e) => handlePermissionChange(permission.ScreenID, 'CanEdit', e.target.checked)}
                                  className="h-4 w-4 text-[#6A89A7] rounded focus:ring-[#BDDDFC]"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={permission.CanDelete || false}
                                  onChange={(e) => handlePermissionChange(permission.ScreenID, 'CanDelete', e.target.checked)}
                                  className="h-4 w-4 text-[#6A89A7] rounded focus:ring-[#BDDDFC]"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <button
                                  type="button"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveScreen(permission.ScreenID)}
                                >
                                  <TrashIcon />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : (initialData ? 'Update Profile' : 'Create Profile')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileFormModal;